/**
 * Generate a UUID version 7 (time-ordered UUID).
 *
 * UUID v7 format:
 * - 48 bits: Unix timestamp in milliseconds
 * - 4 bits: Version (0111 for v7)
 * - 12 bits: Random data
 * - 2 bits: Variant (10 for RFC 4122)
 * - 62 bits: Random data
 *
 * @returns A UUIDv7 string in standard format (8-4-4-4-12)
 */
export function generateUUID7(): string {
  const timestamp = Date.now();

  // Generate random bytes (need 10 bytes: 2 for version field, 2 for variant field, 6 for node)
  const randomBytes = new Uint8Array(10);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(randomBytes);
  } else {
    // Fallback for environments without crypto
    for (let i = 0; i < 10; i++) {
      randomBytes[i] = Math.floor(Math.random() * 256);
    }
  }

  const hex = (n: number, len: number) => n.toString(16).padStart(len, "0");
  const byteToHex = (b: number) => b.toString(16).padStart(2, "0");

  // First 48 bits: timestamp (split into 32-bit high + 16-bit low)
  const timeHigh = Math.floor(timestamp / 0x10000);
  const timeLow = timestamp & 0xffff;

  // time-high (32 bits) = first 8 hex chars
  const timeHighHex = hex(timeHigh, 8);
  // time-low (16 bits) = next 4 hex chars
  const timeLowHex = hex(timeLow, 4);

  // Next 16 bits: 4-bit version (0111) + 12-bit random
  const versionAndRandom = 0x7000 | (((randomBytes[0]! << 4) | (randomBytes[1]! >> 4)) & 0x0fff);
  const versionHex = hex(versionAndRandom, 4);

  // Next 16 bits: 2-bit variant (10) + 14-bit random
  const variantAndRandom =
    0x8000 |
    ((((randomBytes[1]! & 0x0f) << 10) | (randomBytes[2]! << 2) | (randomBytes[3]! >> 6)) & 0x3fff);
  const variantHex = hex(variantAndRandom, 4);

  // Last 48 bits: random (6 bytes)
  const nodeHex =
    byteToHex(randomBytes[4]!) +
    byteToHex(randomBytes[5]!) +
    byteToHex(randomBytes[6]!) +
    byteToHex(randomBytes[7]!) +
    byteToHex(randomBytes[8]!) +
    byteToHex(randomBytes[9]!);

  return `${timeHighHex}-${timeLowHex}-${versionHex}-${variantHex}-${nodeHex}`;
}
